import { useState } from "react";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { InputPanel } from "@/components/InputPanel";
import { ResultsDisplay } from "@/components/ResultsDisplay";
import { ComparisonTable } from "@/components/ComparisonTable";
import { PayPeriodSelector } from "@/components/PayPeriodSelector";
import { useTaxCalculator, useCountryComparison } from "@/hooks/useTaxCalculator";
import { type CountryCode, type PayPeriod, countries } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Globe, Calculator, TrendingUp, Shield, FileText, DollarSign, BarChart3, Code, CheckCircle2, User, ExternalLink, ChevronDown } from "lucide-react";

const DEFAULT_SALARIES: Record<string, number> = {
  uk: 50000, us: 75000, ca: 70000, au: 80000, de: 50000,
  nl: 50000, ae: 150000, sg: 80000, ch: 100000, ie: 50000,
  fr: 40000, es: 30000, it: 30000, se: 450000, no: 650000,
  dk: 500000, nz: 70000, jp: 5000000, pt: 25000, be: 40000,
  at: 40000, pl: 80000, hk: 300000, kr: 40000000, sa: 120000,
  br: 80000, mx: 200000, in: 1000000, fi: 45000, cz: 800000,
  hu: 5000000, gr: 25000, qa: 200000, bh: 20000, kw: 20000,
  om: 20000, th: 600000, my: 60000, id: 120000000,
  za: 300000, ng: 5000000, ke: 1200000, il: 200000, ru: 1500000, tr: 1200000,
};

function CountrySelector() {
  const [isOpen, setIsOpen] = useState(false);
  const sorted = [...countries].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="relative">
      <Button
        variant="outline"
        className="w-full justify-between"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="country-selector-button"
      >
        <span className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          <span>Select Country ({countries.length})</span>
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 max-h-96 overflow-y-auto rounded-md border border-border bg-popover p-2 shadow-lg animate-in fade-in-0 zoom-in-95">
          {sorted.map(country => (
            <Link
              key={country.code}
              href={`/calculator/${country.code}`}
              className="flex items-center gap-3 px-3 py-2 rounded hover:bg-accent transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <span className="text-2xl">{country.flag}</span>
              <span className="font-medium">{country.name}</span>
              <span className="text-xs text-muted-foreground ml-auto">{country.currency}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const [salary, setSalary] = useState<number>(50000);
  const [countryCode, setCountryCode] = useState<CountryCode>("uk");
  const [payPeriod, setPayPeriod] = useState<PayPeriod>("yearly");
  const [displayPeriod, setDisplayPeriod] = useState<PayPeriod>("yearly");
  const [stateCode, setStateCode] = useState<string>("CA");

  const handleCountryChange = (code: CountryCode) => {
    setCountryCode(code);
    setSalary(DEFAULT_SALARIES[code] ?? 50000);
  };

  const result = useTaxCalculator(salary, payPeriod, countryCode, stateCode);
  const comparisonResults = useCountryComparison(salary, payPeriod, stateCode);

  return (
    <>
      <Helmet>
        <title>Free Salary Tax Calculator 2026 | Accurate Take-Home Pay - TaxYourSalary</title>
        <meta name="description" content="Calculate your take-home pay after tax for 2026 (UK/AU/NZ: 2026/2027). Free, accurate salary tax calculator for 45 countries: UK, USA, France, Germany, India, Brazil and more. See your estimated net pay after tax in seconds." />
        <meta name="keywords" content="salary calculator 2026, tax calculator, take-home pay, income tax 2026, net salary, after tax calculator, UK salary after tax, US tax calculator 2026, salary tax brackets 2026" />
        <link rel="canonical" href="https://taxyoursalary.com/" />
        <link rel="icon" type="image/png" sizes="48x48" href="/favicon-48.png" />
        <link rel="icon" type="image/png" sizes="192x192" href="/favicon-192.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <meta name="msapplication-TileImage" content="/favicon-192.png" />
        
        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-8340HB0F5E"></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-8340HB0F5E');
          `}
        </script>

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://taxyoursalary.com/" />
        <meta property="og:title" content="Free Salary Tax Calculator 2026 | TaxYourSalary.com" />
        <meta property="og:description" content="Calculate your estimated take-home pay after tax for 45 countries. Free, accurate salary calculator with official 2026 rates (UK/AU/NZ: 2026/2027)." />
        <meta property="og:site_name" content="TaxYourSalary.com" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Salary Tax Calculator 2026 | TaxYourSalary.com" />
        <meta name="twitter:description" content="Calculate your estimated take-home pay after tax for 45 countries. Free, accurate salary calculator with official 2026 rates (UK/AU/NZ: 2026/2027)." />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="TaxYourSalary.com" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="manifest" href="/site.webmanifest" />

        {/* FAQ Schema for Position Zero / Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I calculate my take-home pay after tax?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Enter your gross salary into our free calculator, select your country (UK, USA, Canada, etc.), and instantly see your net pay after income tax and social contributions are deducted. Our calculator uses official 2026 tax rates (UK/AU/NZ: 2026/2027)."
                }
              },
              {
                "@type": "Question",
                "name": "What is the UK income tax rate for 2026/2027?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The UK tax rates for 2026/2027 are: 0% on income up to £12,570 (Personal Allowance), 20% Basic Rate on £12,571-£50,270, 40% Higher Rate on £50,271-£125,140, and 45% Additional Rate above £125,140."
                }
              },
              {
                "@type": "Question",
                "name": "Which country has the lowest income tax?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Among major economies, the UAE has 0% income tax for most workers. Singapore and Switzerland also have relatively low tax rates compared to Western European countries and the USA."
                }
              },
              {
                "@type": "Question",
                "name": "How accurate is this salary tax calculator?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our calculator uses verified 2026/2027 government tax rates and includes income tax, National Insurance (UK), Social Security (USA), and other mandatory deductions based on official sources."
                }
              }
            ]
          })}
        </script>

        {/* SoftwareApplication Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "TaxYourSalary - Free Salary Tax Calculator",
            "url": "https://taxyoursalary.com",
            "applicationCategory": "FinanceApplication",
            "operatingSystem": "Web",
            "description": "Free salary tax calculator for 45 countries. Calculate your estimated take-home pay after income tax and social contributions instantly using 2026/2027 rates.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          })}
        </script>

        {/* WebSite Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "TaxYourSalary",
            "url": "https://taxyoursalary.com",
            "description": "Free salary tax calculator for 45 countries with 2026/2027 tax rates"
          })}
        </script>

        {/* Organization Schema - Google Knowledge Panel */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "TaxYourSalary",
            "url": "https://taxyoursalary.com",
            "logo": "https://taxyoursalary.com/favicon-192.png",
            "description": "Free, accurate salary tax calculator for 45 countries including UK, USA, France, Germany, India, Brazil, Japan, Australia, Netherlands, Singapore, Switzerland and more.",
            "sameAs": [
              "https://taxyoursalary.com"
            ]
          })}
        </script>

        {/* WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Free Salary Tax Calculator 2026 | Accurate Take-Home Pay - TaxYourSalary",
            "description": "Calculate your 2026/2027 take-home pay instantly. Free, accurate salary tax calculator for 45 countries: UK, USA, France, Germany, India, Brazil and more. See your estimated net pay after tax in seconds.",
            "url": "https://taxyoursalary.com/",
            "inLanguage": "en",
            "isPartOf": {
              "@type": "WebSite",
              "url": "https://taxyoursalary.com",
              "name": "TaxYourSalary"
            }
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
      
      <main className="flex-1">
        <section className="bg-emerald-600">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
              <span className="text-sm font-medium text-emerald-50">
                NEW · Budget &amp; Money Planner — see what's actually left after your bills, then how to split it
              </span>
              <Link href="/budget-planner" data-testid="link-budget-hero">
                <Button size="sm" className="!bg-white hover:!bg-emerald-50 !text-emerald-700 font-semibold">
                  Try the planner free
                </Button>
              </Link>
            </div>
          </div>
        </section>
        <section className="py-12 md:py-16 lg:py-20 border-b border-border bg-gradient-to-b from-background to-muted/30">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
                Free Salary Tax Calculator{" "}
                <span className="text-primary">2026</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Calculate your estimated take-home pay after income tax for 45 countries — UK, USA, France, Germany, India, Brazil, Japan and more — using official 2026/2027 tax rates.
              </p>
            </div>

            <div className="grid lg:grid-cols-[380px_1fr] gap-6 lg:gap-8 overflow-hidden">
              <div className="min-w-0 overflow-hidden">
                <InputPanel
                  salary={salary}
                  setSalary={setSalary}
                  countryCode={countryCode}
                  setCountryCode={handleCountryChange}
                  payPeriod={payPeriod}
                  setPayPeriod={setPayPeriod}
                  stateCode={stateCode}
                  setStateCode={setStateCode}
                />
              </div>

              <div className="space-y-6 min-w-0">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold">Results</h2>
                    <span 
                      className="inline-flex items-center gap-1.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full"
                      data-testid="status-live-rates"
                    >
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Live Rates
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Display as:</span>
                    <PayPeriodSelector
                      value={displayPeriod}
                      onChange={setDisplayPeriod}
                      className="!gap-0.5"
                    />
                  </div>
                </div>
                
                <ResultsDisplay 
                  result={result} 
                  displayPeriod={displayPeriod}
                />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border/50">
                  <div className="flex flex-col items-center text-center p-2">
                    <Shield className="h-5 w-5 text-primary mb-1" />
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Official Data</span>
                    <span className="text-xs font-medium">2026/2027 Rates</span>
                  </div>
                  <div className="flex flex-col items-center text-center p-2">
                    <Calculator className="h-5 w-5 text-primary mb-1" />
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Accuracy</span>
                    <span className="text-xs font-medium">Expert Reviewed</span>
                  </div>
                  <div className="flex flex-col items-center text-center p-2">
                    <TrendingUp className="h-5 w-5 text-primary mb-1" />
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Last Updated</span>
                    <span className="text-xs font-medium">May 2026</span>
                  </div>
                  <div className="flex flex-col items-center text-center p-2">
                    <Globe className="h-5 w-5 text-primary mb-1" />
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Global</span>
                    <span className="text-xs font-medium">10+ Countries</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-2">
                  Compare Across Countries
                </h2>
                <p className="text-muted-foreground">
                  See how your salary compares across all 45 countries we support
                </p>
              </div>
              <Link href="/compare" data-testid="link-compare-all">
                <Button variant="outline" className="gap-2" data-testid="button-view-all-countries">
                  View All 46 Countries
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            {/* Show top 10 countries on homepage, full list on /compare */}
            <ComparisonTable 
              results={comparisonResults.slice(0, 10)}
              displayPeriod={displayPeriod}
              baseCountryCode={countryCode}
            />
          </div>
        </section>

        <section className="py-12 md:py-16 bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Calculate for Any Country
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We support tax calculations for 45 countries with official 2026/2027 rates
              </p>
            </div>

            <div className="space-y-6">
              {/* Featured Countries - Always Visible */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                  Popular Countries
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {["uk", "us", "ca", "au", "de", "fr", "ae", "sg", "ch", "ie", "nl", "jp"].map(code => {
                    const country = countries.find(c => c.code === code);
                    if (!country) return null;
                    return (
                      <Link
                        key={country.code}
                        href={`/calculator/${country.code}`}
                        className="flex flex-col items-center gap-2 px-3 py-3 rounded-lg border border-border bg-background hover:border-primary/50 hover:bg-accent transition-colors group"
                      >
                        <span className="text-3xl group-hover:scale-110 transition-transform">{country.flag}</span>
                        <span className="text-sm font-medium text-center leading-tight">{country.name}</span>
                        <span className="text-xs text-muted-foreground">{country.currency}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* All Countries Dropdown */}
              <div className="pt-4 border-t border-border/50">
                <p className="text-sm text-muted-foreground text-center mb-4">
                  {countries.length - 12} more countries available
                </p>
                <CountrySelector />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Calculator className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Accurate Calculations</h3>
                  <p className="text-sm text-muted-foreground">
                    Based on official government tax rates for 2026/2027 tax years
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Globe className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">10 Countries</h3>
                  <p className="text-sm text-muted-foreground">
                    Compare salaries across UK, USA, Canada, Australia, and more
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Free Forever</h3>
                  <p className="text-sm text-muted-foreground">
                    No sign-up required. Calculate as many salaries as you want
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 border-t border-border">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Expert Tax Guides
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Learn everything about tax systems with our in-depth country guides
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link href="/blog/uk-vs-usa-tax-comparison" data-testid="link-uk-usa-guide">
                <Card className="hover-elevate cursor-pointer h-full">
                  <CardContent className="p-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <Globe className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">UK vs USA Tax Comparison</h3>
                    <p className="text-xs text-muted-foreground">Compare tax systems side by side</p>
                  </CardContent>
                </Card>
              </Link>
              <Link href="/blog/highest-paying-countries-after-tax" data-testid="link-highest-paying-guide">
                <Card className="hover-elevate cursor-pointer h-full">
                  <CardContent className="p-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <DollarSign className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Highest Paying Countries</h3>
                    <p className="text-xs text-muted-foreground">Where to maximize your net salary</p>
                  </CardContent>
                </Card>
              </Link>
              <Link href="/blog/national-insurance-explained-uk" data-testid="link-ni-guide">
                <Card className="hover-elevate cursor-pointer h-full">
                  <CardContent className="p-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">UK National Insurance</h3>
                    <p className="text-xs text-muted-foreground">NICs explained for employees</p>
                  </CardContent>
                </Card>
              </Link>
              <Link href="/blog/tax-brackets-explained" data-testid="link-tax-brackets-guide">
                <Card className="hover-elevate cursor-pointer h-full">
                  <CardContent className="p-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <BarChart3 className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1">Tax Brackets Explained</h3>
                    <p className="text-xs text-muted-foreground">How progressive taxation works</p>
                  </CardContent>
                </Card>
              </Link>
            </div>

            <div className="text-center mt-8">
              <Link href="/blog" data-testid="link-view-all-guides">
                <Button variant="outline" className="gap-2" data-testid="button-view-all-guides">
                  View All Guides
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-primary text-primary-foreground">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Compare Multiple Countries
            </h2>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              Planning to relocate? Compare your salary across different countries side by side.
            </p>
            <Link href="/compare">
              <Button size="lg" variant="secondary" className="gap-2">
                Compare Countries
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Expert Review / E-E-A-T Section */}
        <section className="py-12 md:py-16 border-t border-border">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-2 mb-6">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400" data-testid="status-expert-reviewed">Sourced &amp; reviewed</span>
              </div>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="h-8 w-8 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-1">Reviewed by the TaxYourSalary Editorial Team</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Every rate in our engine is traced to a primary source published by the relevant revenue authority, and each calculator page names that source alongside the date our figures were last compared against it. Our review process, the sources we use and how we handle corrections are set out in our{" "}
                        <a href="/editorial-policy" className="text-primary underline underline-offset-2">editorial policy</a>.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">
                          <Shield className="h-3 w-3" /> HMRC Data
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">
                          <Shield className="h-3 w-3" /> IRS Data
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">
                          <Shield className="h-3 w-3" /> Source &amp; check date on every page
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <p className="text-xs text-muted-foreground mt-4 text-center">
                Calculations are estimates for informational purposes. Always consult a qualified tax professional for personalized advice.
              </p>
            </div>
          </div>
        </section>

        {/* Embeddable Widget Section */}
        <section className="py-12 md:py-16 bg-muted/30 border-t border-border">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Code className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-bold">Embed Our Calculator</h2>
              </div>
              <p className="text-muted-foreground mb-6">
                Add our free salary calculator to your website or blog. Copy the code below to embed it.
              </p>
              
              <Card>
                <CardContent className="p-4">
                  <div className="bg-muted rounded-lg p-4 font-mono text-xs text-left overflow-x-auto">
                    <code className="text-foreground whitespace-pre-wrap break-all">
{`<!-- TaxYourSalary Calculator Widget -->
<iframe 
  src="https://taxyoursalary.com/embed" 
  width="100%" 
  height="500" 
  frameborder="0"
  title="Salary Tax Calculator"
></iframe>
<p style="font-size:12px;text-align:center;">
  Powered by <a href="https://taxyoursalary.com" target="_blank" rel="noopener">TaxYourSalary.com</a>
</p>`}
                    </code>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ExternalLink className="h-4 w-4" />
                      <span>Free to use with attribution</span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(`<!-- TaxYourSalary Calculator Widget -->\n<iframe src="https://taxyoursalary.com/embed" width="100%" height="500" frameborder="0" title="Salary Tax Calculator"></iframe>\n<p style="font-size:12px;text-align:center;">Powered by <a href="https://taxyoursalary.com" target="_blank" rel="noopener">TaxYourSalary.com</a></p>`);
                      }}
                      data-testid="button-copy-embed"
                    >
                      Copy Code
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <p className="text-xs text-muted-foreground mt-4">
                By embedding this widget, you agree to keep the attribution link visible.
              </p>
            </div>
          </div>
        </section>
      </main>

        <Footer />
      </div>
    </>
  );
}
